import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  Settings as SettingsIcon,
  Building2,
  Users2,
  Database,
  AlertTriangle,
  Sparkles,
  Rocket,
  CreditCard,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ITEMS = [
  {
    key: "has_org_settings",
    title: "Org settings completed",
    desc: "Org name and retention policy in place.",
    icon: SettingsIcon,
    actionLabel: "Open settings",
    actionTo: "/settings",
    testid: "prelaunch-org-settings",
  },
  {
    key: "has_data_controller",
    title: "Data controller details populated",
    desc: "GDPR-required name + address; renders on your Article 30 PDF and the public privacy notice.",
    icon: Shield,
    actionLabel: "Open settings",
    actionTo: "/settings",
    testid: "prelaunch-data-controller",
  },
  {
    key: "has_sites",
    title: "At least one site created",
    desc: "A physical location for visitors to sign in.",
    icon: Building2,
    actionLabel: "Manage sites",
    actionTo: "/sites",
    testid: "prelaunch-sites",
  },
  {
    key: "has_hosts",
    title: "At least one host invited",
    desc: "Hosts receive notifications when their guests arrive.",
    icon: Users2,
    actionLabel: "Invite a host",
    actionTo: "/users",
    testid: "prelaunch-hosts",
  },
  {
    key: "has_sample_data",
    title: "Sample data loaded (optional)",
    desc: "Explore the product with a Barking Office demo set.",
    icon: Database,
    actionLabel: null,
    actionTo: null,
    optional: true,
    testid: "prelaunch-sample-data",
  },
  {
    key: "mocked_flags_acknowledged",
    title: "MOCKED integrations acknowledged",
    desc: "Stripe & Resend run in mock mode in Phase 0 — confirm you understand.",
    icon: AlertTriangle,
    actionLabel: null,
    actionTo: null,
    custom: "acknowledge",
    testid: "prelaunch-mocked-ack",
  },
  {
    key: "has_subscription",
    title: "Subscription active or in trial",
    desc: "You're on a paid plan or inside the 14-day free trial.",
    icon: CreditCard,
    actionLabel: "Open billing",
    actionTo: "/billing",
    testid: "prelaunch-subscription",
  },
];

function CompletionRing({ pct }) {
  const size = 112;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  const isFull = pct >= 100;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="prelaunch-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E2E6EF" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={isFull ? "#16A34A" : "#FF6B35"}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 480ms cubic-bezier(.4,.0,.2,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-bold text-navy-900" data-testid="prelaunch-pct">{pct}%</div>
        <div className="text-[10px] uppercase tracking-wider text-navy-500">Ready</div>
      </div>
    </div>
  );
}

export default function PreLaunchChecklist() {
  const [status, setStatus] = useState(null);
  const [acking, setAcking] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  // Load user pref for collapsed state
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/prefs");
        if (data?.prelaunch_card_collapsed) setCollapsed(true);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    const onChanged = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("onboarding-status-changed", onChanged);
    const id = setInterval(load, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("onboarding-status-changed", onChanged);
      clearInterval(id);
    };
  }, [load]);

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post("/onboarding/acknowledge-mocked-flags");
      toast.success("Mocked integrations acknowledged");
      await load();
      window.dispatchEvent(new Event("onboarding-status-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAcking(false);
    }
  };

  if (!status) {
    return (
      <div className="eb-card p-6" data-testid="prelaunch-loading">
        <div className="text-sm text-navy-500">Loading readiness…</div>
      </div>
    );
  }

  const isReady = status.completion_pct >= 100;

  // Once ready and dismissed, render compact view: ring + title + green one-liner only.
  if (isReady && collapsed) {
    return (
      <div className="eb-card p-6" data-testid="prelaunch-collapsed">
        <div className="flex items-start gap-6 flex-wrap md:flex-nowrap">
          <CompletionRing pct={status.completion_pct} />
          <div className="flex-1 min-w-0">
            <div className="eb-overline mb-1.5">Module 18.6 · Pre-launch checklist</div>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight">You're ready to go live.</h2>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-success/10 border border-success/30 text-navy-900 text-sm">
              <Rocket className="w-4 h-4 text-success" />
              All readiness checks pass — flip your kiosk on whenever you're ready.
            </div>
          </div>
          <button
            onClick={async () => {
              setCollapsed(false);
              try { await api.patch("/me/prefs", { prelaunch_card_collapsed: false }); } catch { /* ignore */ }
            }}
            className="btn-ghost text-xs self-start"
            data-testid="prelaunch-expand-btn"
          >
            <ChevronDown className="w-4 h-4" /> Show details
          </button>
        </div>
      </div>
    );
  }

  const dismiss = async () => {
    setCollapsed(true);
    try { await api.patch("/me/prefs", { prelaunch_card_collapsed: true }); } catch { /* ignore */ }
  };

  return (
    <div className="eb-card p-6 md:p-8" data-testid="prelaunch-checklist">
      <div className="flex items-start gap-6 flex-wrap md:flex-nowrap">
        <CompletionRing pct={status.completion_pct} />
        <div className="flex-1 min-w-0">
          <div className="eb-overline mb-1.5">Module 18.6 · Pre-launch checklist</div>
          <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
            {isReady ? "You're ready to go live." : "A few last things before going live."}
          </h2>
          <p className="text-sm text-navy-500 mt-1.5">
            {isReady
              ? "All readiness checks have passed. You can flip your kiosk live whenever you're ready."
              : "Each row updates automatically as you complete it — no need to refresh."}
          </p>

          {status.has_sample_data && (
            <span
              className="inline-flex items-center gap-1.5 mt-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-orange-100 text-orange-700"
              data-testid="prelaunch-demo-tag"
            >
              <Sparkles className="w-3 h-3" /> Demo data loaded
            </span>
          )}
        </div>
      </div>

      {isReady && (
        <div
          className="mt-6 flex items-center gap-3 px-4 py-3 rounded-lg bg-success/10 border border-success/30 text-success"
          data-testid="prelaunch-ready-callout"
        >
          <Rocket className="w-5 h-5" />
          <div className="text-sm font-semibold text-navy-900 flex-1">
            You're ready to go live
            <span className="font-normal text-navy-600 ml-2">— flip your kiosk on whenever you're ready.</span>
          </div>
          <button
            onClick={dismiss}
            className="text-xs text-navy-500 hover:text-navy-900 flex items-center gap-1"
            data-testid="prelaunch-collapse-btn"
          >
            <ChevronUp className="w-4 h-4" /> Collapse
          </button>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-3">
        {ITEMS.map((item) => {
          const done = !!status[item.key];
          const Icon = item.icon;
          return (
            <div
              key={item.key}
              data-testid={item.testid}
              className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                done ? "bg-success/5 border-success/20" : "bg-white border-navy-100"
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  done ? "bg-success/15 text-success" : "bg-navy-50 text-navy-600"
                }`}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium ${done ? "text-navy-500 line-through" : "text-navy-900"}`}>
                    {item.title}
                  </span>
                  {item.optional && !done && (
                    <span className="text-[10px] uppercase tracking-wider text-navy-400 font-semibold">Optional</span>
                  )}
                </div>
                <p className="text-xs text-navy-500 mt-0.5">{item.desc}</p>

                {!done && item.custom === "acknowledge" && (
                  <button
                    onClick={acknowledge}
                    disabled={acking}
                    className="btn-accent mt-2 text-xs px-3 py-1.5"
                    data-testid="prelaunch-ack-btn"
                  >
                    {acking ? "Saving…" : "Got it"}
                  </button>
                )}
                {!done && item.actionTo && (
                  <Link
                    to={item.actionTo}
                    className="inline-block mt-2 text-xs font-semibold text-orange-600 hover:underline"
                    data-testid={`${item.testid}-action`}
                  >
                    {item.actionLabel} →
                  </Link>
                )}
              </div>
              {done ? (
                <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-navy-300 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
